/**
 * Tracks foreground-only elapsed time since a starting reference point.
 * Tabs that are hidden when the timer starts begin paused; the elapsed
 * counter only advances while document.visibilityState === "visible".
 *
 * Without this, a timing includes the full wall-clock time a tab spent
 * backgrounded (up to clampDurationMs's 10-minute ceiling), poisoning p99/max
 * with synthetic peaks for sessions the user wasn't watching.
 */
export interface ForegroundTimer {
  /** Pause point, in ms since the original start, while hidden. */
  pausedAt: number | null;
  /** Sum of completed hidden intervals, in ms. */
  pausedDurationMs: number;
  /** performance.now() value taken at start. */
  startedAt: number;
}

/**
 * Allocate timer state. Pure, with no side effects, so it is safe to call
 * during render. The visibilitychange listener that drives the timer is
 * attached separately by attachForegroundTimerListener() inside a useEffect,
 * so concurrent-render replays and Strict Mode double-invoke can't leak
 * duplicate listeners.
 */
export function createForegroundTimer(): ForegroundTimer {
  const startedAt = performance.now();
  const initiallyHidden = typeof document !== "undefined" && document.hidden;
  return {
    pausedAt: initiallyHidden ? startedAt : null,
    pausedDurationMs: 0,
    startedAt,
  };
}

/** Wires a visibilitychange listener that mutates `timer` in place. Returns the detach function. */
export function attachForegroundTimerListener(timer: ForegroundTimer): () => void {
  if (typeof document === "undefined") return () => {};

  const onVisibilityChange = () => {
    if (document.hidden) {
      if (timer.pausedAt === null) {
        timer.pausedAt = performance.now();
      }
    } else if (timer.pausedAt !== null) {
      timer.pausedDurationMs += performance.now() - timer.pausedAt;
      timer.pausedAt = null;
    }
  };

  document.addEventListener("visibilitychange", onVisibilityChange);
  return () => {
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
}

export function elapsedForeground(timer: ForegroundTimer, now: number): number {
  const wallClock = now - timer.startedAt;
  const currentPaused = timer.pausedAt !== null ? now - timer.pausedAt : 0;
  return wallClock - timer.pausedDurationMs - currentPaused;
}

export interface RunningForegroundTimer {
  elapsedMs: () => number;
  /**
   * A tab hidden for the whole wait reads as about 0 ms, so filter these out
   * of latency percentiles rather than counting them as fast (BRE-9959).
   */
  wasHidden: () => boolean;
  stop: () => void;
}

/** Create and attach together, for callers outside React's render phase. */
export function startForegroundTimer(): RunningForegroundTimer {
  const timer = createForegroundTimer();
  const stop = attachForegroundTimerListener(timer);
  return {
    elapsedMs: () => Math.round(elapsedForeground(timer, performance.now())),
    wasHidden: () => timer.pausedAt !== null || timer.pausedDurationMs > 0,
    stop,
  };
}
